/**
Copyright (C) 2019-2022, Infraclear Inc. All Rights Reserved.
*/

import React, { useEffect, useState } from "react";
import instance from "../../utils/axiosInstance";
import "./navbar.scss";
import { LogoutUser, UserDetails } from "../../constants/api-urlConstants";
import { Avatar, Box, Menu, MenuItem, Tab, Tabs } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { ConfirmationBox } from "../ConfirmationBox/ConfirmationBox";
import { MessageText } from "../../constants/messageText";
import { ResetPasswordPage } from "../../pages/resetPassword/resetPassword";
import { useLocation } from 'react-router';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import PeopleOutlineIcon from '@mui/icons-material/PeopleOutline';
import AppsOutlinedIcon from '@mui/icons-material/AppsOutlined';
import { HelpPopup } from "../helpPopup/helpPopup";
import { RolePersmission } from "../../context/RolePermissionContext";
import { PermissionKey, ROLES } from "../../constants/commonConstants";
import { TimerValusSet } from "../../context/TimerContext";
import { handleSaveUserInfo, redirectToRespectiveAppByRoles } from "../../utils";
import { Settings } from "@mui/icons-material";

export const Navbar: React.FC<any> = () => {
  const navigate = useNavigate();
  const [userData, setUserData] = useState<any>({});
  const [showConfirmation, setShowConfirmation] = React.useState(false);
  const [openResetPass, setOpenResetPass] = React.useState(false);
  const [isTrialUser, setIsTrialUser] = React.useState<any>(false);
  const [msgConfig, setMsgConfig] = React.useState({
    popupTitle: '',
    popupDesc: '',
    primaryBtnTxt: '',
    secondaryBtnTxt: '',
  });
  const currentTab = localStorage.getItem('currentTab');
  const [selectedTab, setSelectedTab] = React.useState<any>(currentTab ? currentTab : 'applications');
  const [value, setValue] = React.useState<number | undefined>(undefined);
  const location = useLocation();
  const { setUserHeartBeat, timeExit } = React.useContext(TimerValusSet);
  const { permissionsObj } = React.useContext(RolePersmission);

  useEffect(() => {
    getUserDetails();
    switch (location.pathname) {
      case '/applications':
        localStorage.setItem('currentTab', location.pathname);
        setValue(0);
        break;
      case '/users':
        localStorage.setItem('currentTab', location.pathname);
        if (permissionsObj.hasOwnProperty(PermissionKey.AUTH_USERS_TAB_VIEW)) {
          setValue(1);
        }
        break;
      case '/config':
        localStorage.setItem('currentTab', location.pathname);
        setValue(2);
        break;
      case '/users/usersEnrolment':
        localStorage.setItem('currentTab', location.pathname);
        if (permissionsObj.hasOwnProperty(PermissionKey.AUTH_USERS_TAB_VIEW)) {
          setValue(1);
        }
         break;
      default:
        localStorage.setItem('currentTab', '/applications');
        setValue(0);
        break;
    }

  }, [location.pathname, permissionsObj.hasOwnProperty(PermissionKey.AUTH_USERS_TAB_VIEW)]);

  const [anchorElUser, setAnchorElUser] = React.useState<null | HTMLElement>(
    null
  );

  const handleOpenUserMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElUser(event.currentTarget);
  };

  const logoutConfirm = () => {
    setAnchorElUser(null);
    setMsgConfig({
      popupTitle: MessageText.LOGOUT_TITLE,
      popupDesc: MessageText.LOGOUT_DESC,
      primaryBtnTxt: MessageText.LOGOUT_TXT,
      secondaryBtnTxt: MessageText.CANCEL_TXT,
    });
    setShowConfirmation(true);
  }

  const openResetPassItem = () => {
    setAnchorElUser(null);
    setOpenResetPass(true);
  }

  const onConfirmAction = (value: any) => {
    if (value) {
      setShowConfirmation(false);
      logoutUser();
    } else {
      setShowConfirmation(false);
    }
  }

  const handleCloseUserMenu = () => {
    setAnchorElUser(null);
  };

  const logoutUser = () => {
    const USER_API_BASE_URL = `${LogoutUser}`;
    instance
      .get(USER_API_BASE_URL)
      .then((response) => {
        localStorage.clear();
        if (userData?.roles?.includes(ROLES.SSO_USER)) {
          navigate("/sso?logout=true");
        } else {
          navigate("/login");
        }
      })
      .catch((error) => {
        localStorage.clear();
        if (userData?.roles?.includes(ROLES.SSO_USER)) {
          navigate("/sso?logout=true");
        } else {
          navigate("/login");
        }
      });
  }

  const getUserDetails = () => {
    const USER_API_BASE_URL = `${UserDetails}`;
    instance
      .get(USER_API_BASE_URL)
      .then((response) => {
        setUserHeartBeat(timeExit());
        const res = response.data;
        setUserData(res.data);
        redirectToRespectiveAppByRoles(res?.data?.roles || []);
        const isAdmin_value = res?.data?.roles && res?.data?.roles.length > 0 && res?.data?.roles.includes("SUPER_ADMIN");
        setIsTrialUser(res?.data?.roles && res?.data?.roles.length > 0 && res?.data?.roles.includes("DOC_SEARCH_DEMO_USER"));
        const userInfo: any = {
          id: res?.data.id,
          roles: res?.data.roles,
          isAdmin: isAdmin_value
        }
        handleSaveUserInfo(userInfo);
      })
      .catch((error) => {
      });
  };

  const onTabChanged = (event: React.SyntheticEvent, newValue: number) => {
    switch (newValue) {
      case 0:
        setSelectedTabChange('/applications');
        break;
      case 1:
        setSelectedTabChange('/users');
        break;
      case 2:
        setSelectedTabChange('/config');
        break;
      default:
        setSelectedTabChange('/applications');
        break;
    }
    setValue(newValue);
  };

  const setSelectedTabChange = (tab: any) => {
    setSelectedTab(tab);
    localStorage.setItem('currentTab', tab);
    navigate(tab);
  }
  function a11yProps(index: number) {
    return {
      id: `simple-tab-${index}`,
      'aria-controls': `simple-tabpanel-${index}`,
    };
  }

  function stringAvatar(name: string) {
    return {
      children: `${name.split(' ')[0][0]}${name.split(' ')[1][0]}`,
    };
  }

  return (
    <div className="navbar">
      <div className="wrapper">
        <div className="top">
          <img alt=""
            className="imgLogoStyle"
            src={require("../../assets/images/infraclearlogo.png")}
          />
        </div>
        <div className="menuTabsWrapper">
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            {value !== undefined &&
              <Tabs
                value={value}
                onChange={onTabChanged}
                className="allTabs"
              >
                <Tab
                  className="tabs"
                  label={
                    <React.Fragment >
                      <div className="menuTab">
                        <span className="usersText"  >My Applications</span>
                        <span className="userLogo">
                          <AppsOutlinedIcon />
                        </span>
                      </div>
                    </React.Fragment>
                  }
                  {...a11yProps(0)}
                />
                {permissionsObj[PermissionKey.AUTH_USERS_TAB_VIEW] &&
                  <Tab
                    className="tabs"
                    label={
                      <React.Fragment >
                        <div className="menuTab">
                          <span className="usersText" >All Users</span>
                          <span className="userLogo">
                            <PeopleOutlineIcon />
                          </span>
                        </div>
                      </React.Fragment>
                    }
                    {...a11yProps(1)}
                  />
                }
                {(permissionsObj[PermissionKey.AUTH_CONFIG_TAB_VIEW]) &&
                  <Tab
                    className="tabs"
                    label={
                      <React.Fragment >
                        <div className="menuTab">
                          <span className="usersText" >Config</span>
                          <span className="userLogo">
                            <Settings />
                          </span>
                        </div>
                      </React.Fragment>
                    }
                    {...a11yProps(2)}
                  />
                }
              </Tabs>
            }
          </Box>
        </div>
        <div className="navBarRight">
          <div className="items">
            <Box className="items" sx={{ flexGrow: 0 }} >
              <HelpPopup />
              <div className="profilename" onClick={handleOpenUserMenu}>
                <Avatar className="avatarProfileName" {...stringAvatar(`${userData?.firstName} ${userData?.lastName}`)} />
                <span className="textProfileName"> {userData?.firstName} {userData?.lastName}</span>
              </div>
              <div className="avtarText" onClick={handleOpenUserMenu}>
                <KeyboardArrowDownIcon />
              </div>
              <Menu
                sx={{ mt: "35px" }}
                id="menu-appbar"
                className="menulist"
                anchorEl={anchorElUser}
                anchorOrigin={{
                  vertical: "top",
                  horizontal: "right",
                }}
                keepMounted
                transformOrigin={{
                  vertical: "top",
                  horizontal: "right",
                }}
                open={Boolean(anchorElUser)}
                onClose={handleCloseUserMenu}
              >
                <MenuItem
                  onClick={openResetPassItem}
                  className="liMenu"
                >
                  <span className="menuItemValue">
                    Reset Password
                  </span>
                </MenuItem>
                <MenuItem
                  onClick={logoutConfirm}
                  className="liMenu"
                >
                  <span className="menuItemValue">
                    Logout
                  </span>
                </MenuItem>
              </Menu>
            </Box>
          </div>
        </div>
      </div>
      {showConfirmation &&
        <ConfirmationBox openConfirmation={showConfirmation} closeConfirmation={onConfirmAction} msgConfig={msgConfig} />
      }
      {openResetPass &&
        <ResetPasswordPage openReset={openResetPass} trialValue={isTrialUser} closePopup={(value: boolean) => { setOpenResetPass(false) }} />
      }
    </div>
  );
};
